import {
  Cartesian3,
  Color,
  ColorMaterialProperty,
  Rectangle,
  BoundingSphere,
  HeadingPitchRange,
  Math as CesiumMath,
  HeightReference,
} from 'cesium';
import { useEffect, useRef, useState } from 'react';
import { parseGeoData } from "senf-shared";
import { flyToFeature } from '../mapBuilder/components/cesium/cesium-camera';

interface PinData {
  ideaId?: string;
  long?: number;
  lat?: number;
  color?: string;
  geoData?: any;
}

interface UseIdeaPinCesiumProps {
  cesiumViewer: any; // Cesium.Viewer instance
  pinData: PinData | null;
  setIdeaMarkerColor: (color: string) => void;
  paddings?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  setGisData: (data: any[]) => void;
  normalizedGisData: any[];
}

const useIdeaPinCesium = ({
  cesiumViewer,
  pinData,
  setIdeaMarkerColor,
  paddings,
  setGisData,
  normalizedGisData
}: UseIdeaPinCesiumProps) => {
  const pinEntityRef = useRef<any>(null);
  const lastFlownPinRef = useRef<string | null>(null);
  const pinDataRef = useRef<PinData | null>(pinData);
  const [flyTrigger, setFlyTrigger] = useState(0);
  
  // Update the pinDataRef when pinData changes and trigger flying
  useEffect(() => {
    pinDataRef.current = pinData;
    // Increment the trigger to cause the flying effect to run
    setFlyTrigger(prev => prev + 1);
  }, [pinData]);
  
  // Function to set selected entity visibility
  const setSelectedEntity = (viewer: any, ideaId?: string) => {
    if (!viewer) return;

    // Get all entities in the viewer
    const entities = viewer.entities.values;
    
    // If no ideaId, show all entities
    if (!ideaId) {
      entities.forEach((entity: any) => {
        if (entity.ideaId) {
          entity.show = true;
        }
      });
      return;
    }

    // Show only the selected entity
    entities.forEach((entity: any) => {
      if (entity.ideaId) {
        entity.show = entity.ideaId === ideaId;
      }
    });
  };

  // Function to set entity opacity
  const setEntityOpacity = (viewer: any, opacity: number) => {
    if (!viewer) return;

    const entities = viewer.entities.values;
    entities.forEach((entity: any) => {
      if (entity.ideaId) {
        if (entity.polygon) {
          entity.polygon.material = new ColorMaterialProperty(
            Color.WHITE.withAlpha(opacity)
          );
        }
        if (entity.polyline) {
          entity.polyline.material = new ColorMaterialProperty(
            Color.WHITE.withAlpha(opacity)
          );
        }
        if (entity.point) {
          entity.point.color = Color.WHITE.withAlpha(opacity);
        }
      }
    });
  };

  // Function to check if we should fly to this pin
  const shouldFlyToPin = (pin: PinData | null): boolean => {
    if (!pin) return false;
    
    // If we've never flown to a pin before, we should fly
    if (!lastFlownPinRef.current) return true;
    
    // If the pin has an ideaId and it's different from the last one we flew to, we should fly
    if (pin.ideaId && pin.ideaId !== lastFlownPinRef.current) return true;
    
    // If the pin has coordinates and they're different from the last one we flew to, we should fly
    if (pin.long && pin.lat) {
      const lastPin = lastFlownPinRef.current;
      // We can't compare coordinates directly, so we'll just fly if the ideaId is different
      return pin.ideaId !== lastPin;
    }
    
    return false;
  };

  // Effect to handle pin entity creation and updates
  useEffect(() => {
    if (!cesiumViewer) return;
    
    const currentPinData = pinDataRef.current;

    // Handle GeoJSON data
    if (currentPinData?.geoData) {
      const parsedGeoData = parseGeoData(currentPinData.geoData);
      setGisData([...normalizedGisData, currentPinData.geoData]);

      // Set entity visibility and opacity
      setSelectedEntity(cesiumViewer, currentPinData.ideaId);
      setEntityOpacity(cesiumViewer, 0.3);
    } else {
      // Remove existing pin if any
      if (pinEntityRef.current) {
        cesiumViewer.entities.remove(pinEntityRef.current);
        pinEntityRef.current = null;
      }

      setSelectedEntity(cesiumViewer);
      setEntityOpacity(cesiumViewer, 1);
    }

    // Handle point pin
    if (currentPinData?.long && currentPinData?.lat) {
      // Remove existing pin if any
      if (pinEntityRef.current) {
        cesiumViewer.entities.remove(pinEntityRef.current);
      }

      // Create new pin entity
      pinEntityRef.current = cesiumViewer.entities.add({
        position: Cartesian3.fromDegrees(currentPinData.long, currentPinData.lat),
        point: {
          pixelSize: 10,
          color: Color.fromCssColorString(currentPinData.color || '#FF0000'),
          outlineColor: Color.WHITE,
          outlineWidth: 2,
          heightReference: HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });

      setIdeaMarkerColor(currentPinData.color || '#FF0000');
      setEntityOpacity(cesiumViewer, 0.3);
    } else {
      // Remove pin and reset view
      if (pinEntityRef.current) {
        cesiumViewer.entities.remove(pinEntityRef.current);
        pinEntityRef.current = null;
      }
      setEntityOpacity(cesiumViewer, 1);
    }

    // Cleanup function
    return () => {
      if (pinEntityRef.current) {
        cesiumViewer.entities.remove(pinEntityRef.current);
        pinEntityRef.current = null;
      }
    };
  }, [cesiumViewer, setIdeaMarkerColor, setGisData, normalizedGisData]);

  // Separate effect to handle flying to pins
  useEffect(() => {
    if (!cesiumViewer) return;
    
    const currentPinData = pinDataRef.current;
    
    // If there's no pin data, reset the last flown pin
    if (!currentPinData) {
      lastFlownPinRef.current = null;
      return;
    }
    
    // Check if we should fly to this pin
    if (!shouldFlyToPin(currentPinData)) {
      return;
    }
    
    // Handle flying to GeoJSON data
    if (currentPinData.geoData) {
      const entity = cesiumViewer.entities.values.find(e => e.ideaId === currentPinData.ideaId);
      if (entity) {
        flyToFeature(cesiumViewer, entity);
        // Update the last flown pin
        lastFlownPinRef.current = currentPinData.ideaId || 'geoData';
      }
    }
    
    // Handle flying to point pin
    if (currentPinData.long && currentPinData.lat) {
        if (pinEntityRef.current) {
          flyToFeature(cesiumViewer, pinEntityRef.current);
        }
      
      // Update the last flown pin
      lastFlownPinRef.current = currentPinData.ideaId || `${currentPinData.long},${currentPinData.lat}`;
    }
  }, [cesiumViewer, flyTrigger]);

  return true;
};

export default useIdeaPinCesium; 